// API Service for Partner Authentication
// Use environment variable for API URL, default to production
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://partner-api.trendingobjects.com/api';

export interface PartnerLoginRequest {
  email: string;
  password: string;
}

export interface PartnerRegisterRequest {
  email: string;
  password: string;
  fullName: string;
  organization: string;
  phone: string;
  country: string;
}

export interface PartnerLoginResponse {
  status: {
    success: boolean;
    code: number;
    message: string;
  };
  data?: {
    token: string;
    partner: {
      _id: string;
      email: string;
      status: string;
      emailVerified: boolean;
      created_at: string;
      updated_at: string;
      profile: {
        fullName: string;
        organization: string;
        phone: string;
      };
      contact: {
        address: {
          country: string;
        };
      };
      settings: {
        notifications: {
          email: boolean;
          sms: boolean;
          push: boolean;
        };
        timezone: string;
        language: string;
      };
      stats: {
        totalTenants: number;
        totalUsers: number;
        totalDevices: number;
        lastActivity: string;
      };
      metadata: {
        source: string;
        tags: string[];
      };
    };
  };
}

export interface PartnerRegisterResponse {
  status: {
    success: boolean;
    code: number;
    message: string;
  };
  data?: {
    partner: {
      _id: string;
      email: string;
      status: string;
      emailVerified: boolean;
      created_at: string;
      updated_at: string;
      profile: {
        fullName: string;
        organization: string;
        phone: string;
      };
      contact: {
        address: {
          country: string;
        };
      };
      settings: {
        notifications: {
          email: boolean;
          sms: boolean;
          push: boolean;
        };
        timezone: string;
        language: string;
      };
      stats: {
        totalTenants: number;
        totalUsers: number;
        totalDevices: number;
        lastActivity: string;
      };
      metadata: {
        source: string;
        tags: string[];
      };
    };
  };
}

class ApiService {
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_URL;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    console.log('🌐 API Service: Making request to:', url);
    console.log('🌐 API Service: Base URL:', this.baseURL);
    
    const defaultHeaders = {
      'Content-Type': 'application/json',
      'accept': '*/*',
    };

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    console.log('🌐 API Service: Request config:', {
      method: config.method,
      headers: config.headers,
      body: config.body ? '***' : undefined
    });

    try {
      const response = await fetch(url, config);
      console.log('🌐 API Service: Response status:', response.status);
      console.log('🌐 API Service: Response OK:', response.ok);
      
      const data = await response.json();
      console.log('🌐 API Service: Response data:', data);
      
      // Check if the response indicates an error (even with 200 status)
      if (data.status && !data.status.success) {
        // This is an API error response, return it as-is
        console.log('🌐 API Service: API returned error response:', data);
        return data;
      }
      
      if (!response.ok) {
        // This is an HTTP error
        console.error('🌐 API Service: HTTP error response:', response.status, data);
        throw new Error(data.status?.message || `HTTP error! status: ${response.status}`);
      }

      console.log('🌐 API Service: Request successful');
      return data;
    } catch (error) {
      console.error('🌐 API Service: Request failed:', error);
      console.error('🌐 API Service: Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      throw error;
    }
  }

  // Partner Login
  async loginPartner(credentials: PartnerLoginRequest): Promise<PartnerLoginResponse> {
    try {
      console.log('🔐 API Service: Making login request to:', `${this.baseURL}/partner/login`);
      console.log('🔐 API Service: Credentials:', { email: credentials.email, password: '***' });
      
      const response = await this.makeRequest<PartnerLoginResponse>('/partner/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
      });

      console.log('🔐 API Service: Raw response:', response);
      console.log('🔐 API Service: Response status:', response.status);
      console.log('🔐 API Service: Response data:', response.data);

      // Store token in localStorage if login successful
      if (response.status?.success && response.data?.token) {
        console.log('🔐 API Service: Storing token in localStorage');
        localStorage.setItem('partnerToken', response.data.token);
        localStorage.setItem('partnerData', JSON.stringify(response.data.partner));
      } else {
        console.warn('🔐 API Service: Login not successful or no token');
        console.warn('🔐 API Service: Status success:', response.status?.success);
        console.warn('🔐 API Service: Token present:', !!response.data?.token);
      }

      return response;
    } catch (error) {
      console.error('🔐 API Service: Login failed with error:', error);
      console.error('🔐 API Service: Error type:', error instanceof Error ? error.constructor.name : typeof error);
      console.error('🔐 API Service: Error message:', error instanceof Error ? error.message : String(error));
      return {
        status: {
          success: false,
          code: 500,
          message: error instanceof Error ? error.message : 'Login failed. Please check your credentials and try again.',
        }
      };
    }
  }

  // Partner Registration
  async registerPartner(partnerData: PartnerRegisterRequest): Promise<PartnerRegisterResponse> {
    try {
      const response = await this.makeRequest<PartnerRegisterResponse>('/partner/register', {
        method: 'POST',
        body: JSON.stringify(partnerData),
      });

      return response;
    } catch (error) {
      console.error('Registration failed:', error);
      return {
        status: {
          success: false,
          code: 500,
          message: error instanceof Error ? error.message : 'Registration failed. Please try again.',
        }
      };
    }
  }

  // Get stored partner data
  getStoredPartnerData() {
    try {
      const partnerData = localStorage.getItem('partnerData');
      return partnerData ? JSON.parse(partnerData) : null;
    } catch (error) {
      console.error('Error retrieving partner data:', error);
      return null;
    }
  }

  // Get stored token
  getStoredToken() {
    return localStorage.getItem('partnerToken');
  }

  // Check if partner is logged in
  isPartnerLoggedIn(): boolean {
    const token = this.getStoredToken();
    const partnerData = this.getStoredPartnerData();
    return !!(token && partnerData);
  }

  // Logout partner
  logoutPartner() {
    localStorage.removeItem('partnerToken');
    localStorage.removeItem('partnerData');
  }

  // Get auth headers for authenticated requests
  getAuthHeaders() {
    const token = this.getStoredToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
}

// Create and export a singleton instance
export const apiService = new ApiService();
export default apiService;
